import React, { useState } from "react";

import { OnboardingStepStatus, OnboardingStep } from "graphql/types";

import { useOnboardingProgress } from "./useOnboardingProgress";
import { useLaunchpadStore, LaunchpadStep } from "./launchpadStore";

import { rootStyle } from "./taskList.css";
import { useActiveCompany } from "components/companies/ActiveCompanyProvider";

import { DefaultLaunchpadTaskSet } from "./tasksets/DefaultLaunchpadTaskSet";
import { BookkeepersTaskSet } from "./tasksets/BookkeepersTaskSet";
import { BrexLaunchpadTaskSet } from "./tasksets/BrexLaunchpadTaskSet";
import { useLaunchpadConfig, LaunchpadType } from "./useLaunchpadConfig";

export const TaskList = ({ fullSize = true }: { fullSize?: boolean }) => {
  const { type } = useLaunchpadConfig();
  const { onboardingProgress, updateOnboardingStep, refetchOnboardingProgress } =
    useOnboardingProgress();
  const { initialIngestCompleted, membership } = useActiveCompany<true>();
  const { showLaunchpadOverlay, goToStep } = useLaunchpadStore();

  const [localProgress, setLocalProgress] = useState<Record<string, OnboardingStepStatus>>({});

  const handleSkip = async (step: OnboardingStep) => {
    setLocalProgress((prev) => ({ ...prev, [step]: OnboardingStepStatus.Skipped }));
    await updateOnboardingStep(step, OnboardingStepStatus.Skipped);
    refetchOnboardingProgress();
  };

  const handleComplete = async (
    step: OnboardingStep,
    status: OnboardingStepStatus | undefined | null
  ) => {
    if (step) {
      // Since we want to "uncheck" the step if it's completed, we need to flip the status
      const finalStepStatus =
        status === OnboardingStepStatus.Completed
          ? OnboardingStepStatus.NotCompleted
          : OnboardingStepStatus.Completed;

      // We want to immediately update progress
      setLocalProgress((prev) => ({ ...prev, [step]: finalStepStatus }));

      await updateOnboardingStep(step, finalStepStatus);
      refetchOnboardingProgress();
    }
  };

  const goToStepAndShow = (
    step: LaunchpadStep,
    status: OnboardingStepStatus | undefined | null
  ) => {
    // Don't go to the step if it's already completed
    if (status === OnboardingStepStatus.Completed) {
      return;
    }

    goToStep(step);
    showLaunchpadOverlay();
  };

  const handleTaskClick = (
    callback: () => void,
    status: OnboardingStepStatus | undefined | null
  ) => {
    if (status === OnboardingStepStatus.Completed) {
      return;
    }
    callback();
  };

  let TaskSet = DefaultLaunchpadTaskSet;
  if (type === LaunchpadType.Brex) {
    TaskSet = BrexLaunchpadTaskSet;
  } else if (type === LaunchpadType.Bookkeepers) {
    TaskSet = BookkeepersTaskSet;
  }

  return (
    <div className={rootStyle}>
      <TaskSet
        fullSize={fullSize}
        initialIngestCompleted={initialIngestCompleted}
        onSkip={handleSkip}
        onComplete={handleComplete}
        onGoToStepAndShow={goToStepAndShow}
        onTaskClick={handleTaskClick}
        localProgress={localProgress}
        onboardingProgress={onboardingProgress}
      />
    </div>
  );
};
